<script lang="ts" setup>
import { API } from "@/api/api";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useSnackbarStore } from "@/store/snackbar.store";
import TruckRoutes from "@/views/trucks/components/TruckRoutes.vue";
import type { TripRouteDto } from "@/api/trips/dto/trip.dto";
import { RouteStatus } from "@/data/trip";
import HandleRouteStatusDialog from "@/views/trucks/components/dialogs/HandleRouteStatusDialog.vue";
import type { CustomerDto } from "@/api/company/dto/customer.dto";

const api = new API();
const route = useRoute();
const { showSnackbarError } = useSnackbarStore();

const routes = ref<TripRouteDto[]>([]);
const customers = ref<CustomerDto[]>([]);
const loading = ref<boolean>(false);
const selectedRoute = ref<TripRouteDto | null>(null);
const showHandleRouteStatusDialog = ref(false);

const truckId = computed(() => {
  return route.params.id;
});

const plannedRoutes = computed(() => {
  return routes.value.filter((route) => route.status === RouteStatus.planned);
});

const currentRoutes = computed(() => {
  return routes.value.filter(
    (route) => route.status === RouteStatus.inProgress || route.status === RouteStatus.notStarted,
  );
});

async function getRoutes(isLoadingCustomers = false) {
  loading.value = true;

  try {
    const response = await api.trucks.getTruckRoutes(truckId.value.toString(), [
      RouteStatus.notStarted,
      RouteStatus.inProgress,
      RouteStatus.planned,
    ]);

    if (response.success && response.data?.routes) {
      routes.value = response.data.routes;
    } else {
      showSnackbarError(`Could not get routes by truck id: ${truckId.value}`);
    }
  } catch (error) {
    console.error(error);
  } finally {
    if (!isLoadingCustomers) {
      loading.value = false;
    }
  }
}

async function getCustomers() {
  loading.value = true;
  try {
    const billToCompanyIds = [
      ...new Set(
        routes.value
          .flatMap((route) => route.travelOrders.map((to) => to.order.billToCustomerId))
          .filter((id): id is number => Boolean(id)),
      ),
    ];

    const response = await api.company.findCustomers({ ids: billToCompanyIds });

    if (response.success && response.data?.items) {
      customers.value = response.data.items;
    } else {
      showSnackbarError(`Could not get customers`);
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await getRoutes(true);
  await getCustomers();
});
</script>

<template>
  <div>
    <template v-if="!loading">
      <div class="font-size-16 text-grey-darken-3 font-weight-500">Current route</div>
      <truck-routes
        v-model:route="selectedRoute"
        v-model:status-dialog="showHandleRouteStatusDialog"
        :customers="customers"
        :routes="currentRoutes"
        class="mt-4"
      >
        <template #noRoutes>
          <span class="text-grey-darken-1">There is no current route for this truck</span>
        </template>
      </truck-routes>

      <div class="font-size-16 text-grey-darken-3 mt-6 font-weight-500">Planned route</div>
      <truck-routes
        v-model:route="selectedRoute"
        v-model:status-dialog="showHandleRouteStatusDialog"
        :customers="customers"
        :routes="plannedRoutes"
        class="mt-4"
      >
        <span class="text-grey-darken-1">There are not planned routes for this truck</span>
      </truck-routes>
    </template>

    <div v-else>
      <div v-for="item in 2" :key="item" class="mb-6">
        <v-skeleton-loader :loading="true" height="45px" width="100%">
          <v-sheet color="grey-lighten-3" height="220px" rounded width="100%"></v-sheet>
        </v-skeleton-loader>

        <v-skeleton-loader :loading="true" class="mt-1" height="110" width="100%">
          <v-sheet color="grey-lighten-3" height="220px" rounded width="100%"></v-sheet>
        </v-skeleton-loader>
      </div>
    </div>

    <handle-route-status-dialog
      v-if="selectedRoute"
      v-model="showHandleRouteStatusDialog"
      :route="selectedRoute"
      @update-routes="getRoutes"
    />
  </div>
</template>

<style lang="scss" scoped></style>
